import { isAuthApiError, isAuthSessionMissingError } from '@supabase/supabase-js'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'

// The ONLY place session-death is handled. A client-cached session can be
// rejected server-side (deleted user, revoked session, forged token); when that
// rejection is OBSERVED, these helpers make sure it ends in a forced sign-out
// at /login instead of an empty CRM. Observation is bounded by the memoization
// below — see `ensureValidSession`.

// Access token already proven against the server; skip repeat round-trips
// so the _authed beforeLoad doesn't pay a network hop on every navigation.
// The cost of that: a token stays trusted for as long as it is the current
// token, however the session ends server-side in the meantime.
let validatedToken: string | null = null

/**
 * Returns the stored session unless it has been proven dead.
 *
 * ONE server round-trip PER ACCESS TOKEN, not per call. An unvalidated or new
 * access token is checked with `getUser()`; a definitive rejection — 401/403,
 * `session_not_found`, PGRST301, or the `AuthSessionMissingError` that auth-js
 * substitutes for `session_not_found` — clears the local session and returns
 * null, so that check now fails closed where it used to fall through to the
 * transient branch. Transient network failures do NOT sign the user out.
 *
 * THIS IS NOT CONTINUOUS VALIDATION, and must not be described as such. Once a
 * token has been validated, the `validatedToken` fast path below returns it
 * from memory on every subsequent call and `getUser()` does not run again until
 * the access token itself changes (refresh, sign-in, sign-out). A session
 * terminated server-side after that point is therefore not observed here until
 * the next token change — Supabase access tokens stay cryptographically valid
 * until they expire, so the open tab keeps working through that window.
 *
 * That trade is deliberate: this runs in the `_authed` route guard, on every
 * navigation, and cannot pay a network hop each time. Immediate or periodic
 * eviction of a server-revoked session from an already-open tab is a separate
 * security/product decision and is NOT provided by this function.
 */
export async function ensureValidSession(): Promise<Session | null> {
  let session: Session | null
  try {
    const result = await supabase.auth.getSession()
    session = result.data.session
  } catch {
    // Reading the stored session is not merely slow-or-fine: it acquires a
    // navigator lock and touches storage, so it can reject outright
    // (NavigatorLockAcquireTimeoutError, a hardened profile refusing storage).
    // Unhandled, that rejection escapes into a route guard's beforeLoad.
    //
    // Fail closed WITHOUT destroying anything: an unreadable session is not a
    // valid session, so grant nothing — but nothing has been proven against
    // the server either, so do not sign the user out. /login is recoverable.
    return null
  }
  if (!session) return null
  if (session.access_token === validatedToken) return session

  let data: Awaited<ReturnType<typeof supabase.auth.getUser>>['data']
  let error: Awaited<ReturnType<typeof supabase.auth.getUser>>['error']
  try {
    ;({ data, error } = await supabase.auth.getUser())
  } catch {
    // Same reasoning as the transient branch below — a throw here says nothing
    // about whether the server still accepts this session, and must not reach
    // the caller as an unhandled rejection.
    return session
  }
  if (data.user) {
    validatedToken = session.access_token
    return session
  }
  if (error && isSessionRejectedError(error)) {
    // Caller (a route guard) decides where to navigate; just drop the session
    validatedToken = null
    await signOutLocally()
    return null
  }
  // Offline or Supabase hiccup: keep the client session, next call re-checks
  return session
}

/**
 * Clears the local session, best effort. Storage can be unavailable, and when
 * it is, the caller's decision — deny access, navigate to /login — is still
 * the right one and must not be abandoned because the cleanup failed.
 */
async function signOutLocally(): Promise<void> {
  try {
    await supabase.auth.signOut({ scope: 'local' })
  } catch {
    /* nothing to do: the navigation the caller performs is what matters */
  }
}

/** Supabase error that means the server no longer accepts this session. */
export function isSessionRejectedError(error: unknown): boolean {
  // THE SHAPE THE SERVER ACTUALLY SENDS. `@supabase/auth-js` intercepts a
  // `session_not_found` response in `lib/fetch.js` and throws
  // `AuthSessionMissingError`, whose `name` is not 'AuthApiError' — so
  // `isAuthApiError` is false for it and every status/code test below is
  // unreachable for the one answer that means the server has thrown this
  // session away. `GoTrueClient._getUser` then hands it back as a RESOLVED
  // result rather than a rejection, so nothing downstream catches it either.
  // Without this line a revoked session is read as an offline hiccup and the
  // user keeps a dead token inside the CRM.
  if (isAuthSessionMissingError(error)) return true
  if (isAuthApiError(error)) {
    return (
      error.status === 401 ||
      error.status === 403 ||
      error.code === 'session_not_found' ||
      error.code === 'refresh_token_not_found'
    )
  }
  // PostgREST rejects an invalid/expired JWT with PGRST301 (HTTP 401)
  if (typeof error === 'object' && error !== null && 'code' in error) {
    return (error as { code?: unknown }).code === 'PGRST301'
  }
  return false
}

let signingOut = false

/**
 * Clears the dead session locally (no server call — the server already
 * rejected it) and hard-navigates to /login. The full page load flushes
 * query cache and all in-memory state from the dead session.
 */
export async function forceSignOutToLogin(): Promise<void> {
  if (signingOut) return
  signingOut = true
  validatedToken = null
  // Ordering is load-bearing: `replace` tears the document down and kills
  // anything still pending, so the local clear has to finish first or the dead
  // token can survive into the next load. It is also best effort — a storage
  // failure must not throw past this point, because that would both skip the
  // navigation and leave `signingOut` latched, silently disabling every later
  // forced sign-out for the lifetime of the page.
  await signOutLocally()
  if (window.location.pathname.startsWith('/login')) {
    signingOut = false
    return
  }
  window.location.replace('/login')
}
